import { useState } from "react";
import type { Pal, Element } from "@/lib/pals-data";
import { ELEMENT_COLORS } from "@/lib/pals-data";
import { CardImage } from "@/components/CardImage";
import { palImage } from "@/lib/pal-images";

/**
 * Portrait resolution order: official TCG card scan -> official Pal artwork ->
 * procedural SVG. If a remote image 404s or is blocked, we drop to the next
 * source instead of leaving a blank box.
 */
export function PalPortrait({
  pal,
  size = 240,
  priority = false,
  artOnly = false,
}: {
  pal: Pal;
  size?: number;
  priority?: boolean;
  /** Prefer square official Pal artwork, useful for fast-moving game targets. */
  artOnly?: boolean;
}) {
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  // Small thumbnails (tables, breeding pairs, damage rows) render the square
  // official artwork rather than the tall card scan, so rows stay compact.
  const compact = size <= 64;
  const card = !artOnly && !compact && pal.tcg?.official && pal.tcg.imageUrl ? pal.tcg.imageUrl : undefined;
  const official = palImage(pal.slug, Math.max(240, Math.round(size)));

  // The box geometry is decided once, from the data — never from load state. A
  // fixed aspect-ratio wrapper means the official scan, the official artwork and
  // the procedural SVG all occupy exactly the same space, so a fallback swap or
  // a slow image can never shift the layout (CLS). `width:100%` + `maxWidth`
  // also stops us painting an image wider than its column on small screens.
  const boxW = Math.round(size);
  const boxH = card ? Math.round((size * 586) / 420) : boxW;

  const inner = (() => {
    if (card && !failed[card]) {
      return (
        <CardImage
          src={card}
          alt={`${pal.name} Palworld TCG card ${pal.tcg.cardNumber} (${pal.tcg.rarityCode})`}
          width={boxW}
          height={boxH}
          priority={priority}
          responsive
          onError={() => setFailed((f) => ({ ...f, [card]: true }))}
          className="h-full w-full rounded-lg"
        />
      );
    }
    if (official && !failed[official]) {
      return (
        <CardImage
          src={official}
          alt={`${pal.name} — official Palworld Pal artwork (${pal.element} element)`}
          width={boxW}
          height={boxH}
          priority={priority}
          responsive
          onError={() => setFailed((f) => ({ ...f, [official]: true }))}
          className="h-full w-full rounded-lg"
        />
      );
    }
    return <PalPortraitSvg pal={pal} size={boxW} />;
  })();

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-lg"
      style={{ width: "100%", maxWidth: boxW, aspectRatio: `${boxW} / ${boxH}` }}
    >
      {inner}
    </div>
  );
}



// Deterministic procedural SVG portrait — SSR-safe, brand-safe.
// Layered: aura -> element motif -> tail -> wings -> body -> limbs -> ears/horns -> face -> spots -> sigil.
function PalPortraitSvg({ pal, size = 240 }: { pal: Pal; size?: number }) {


  const color = ELEMENT_COLORS[pal.element];
  const accent = pal.secondary ? ELEMENT_COLORS[pal.secondary as Element] : color;
  const seed = pal.id * 2654435761;
  const r = (shift: number, mod: number) => (seed >>> shift) % mod;

  const eyeY = 108 + r(4, 14);
  const eyeGap = 30 + r(6, 12);
  const mouthW = 24 + r(8, 24);
  const earTilt = r(12, 40) - 20;
  const cheekY = eyeY + 14;
  const shape = r(2, 6);
  const earStyle = r(14, 5);
  const bodyRot = r(16, 10) - 5;
  const tailStyle = r(20, 4);
  const wingStyle = r(22, 4);
  const limbStyle = r(24, 3);
  const spotStyle = r(26, 4);
  const hornStyle = r(28, 3);
  const bgId = `bg-${pal.id}`;
  const bodyId = `body-${pal.id}`;
  const glowId = `glow-${pal.id}`;

  const bodyPath =
    shape === 0
      ? "M120 210 Q55 210 55 130 Q55 55 120 55 Q185 55 185 130 Q185 210 120 210 Z"
      : shape === 1
        ? "M60 155 Q60 55 120 55 Q180 55 180 155 L180 215 L60 215 Z"
        : shape === 2
          ? "M55 210 L100 55 L140 55 L185 210 Z"
          : shape === 3
            ? "M120 55 C55 55 55 145 55 185 L185 185 C185 145 185 55 120 55 Z"
            : shape === 4
              ? "M120 50 Q60 60 65 140 Q70 210 120 210 Q170 210 175 140 Q180 60 120 50 Z"
              : "M120 48 Q70 48 62 108 Q54 172 90 208 Q120 218 150 208 Q186 172 178 108 Q170 48 120 48 Z";

  // Tail: rendered behind body
  const tail = (() => {
    if (tailStyle === 0) return null;
    if (tailStyle === 1)
      return <path d="M180 190 Q220 180 218 150 Q214 130 200 138" stroke={color} strokeWidth="10" fill="none" strokeLinecap="round" opacity="0.9" />;
    if (tailStyle === 2)
      return (
        <>
          <path d="M60 200 Q30 200 30 170 Q30 150 46 156" stroke={accent} strokeWidth="8" fill="none" strokeLinecap="round" />
          <circle cx="30" cy="170" r="7" fill={accent} />
        </>
      );
    return <path d="M180 200 L222 214 L214 190 L226 178 L204 178 L200 160 Z" fill={accent} opacity="0.85" />;
  })();

  // Wings: rendered behind body
  const wings = (() => {
    if (wingStyle === 0) return null;
    if (wingStyle === 1)
      return (
        <>
          <path d="M60 120 Q10 100 20 170 Q45 150 62 150 Z" fill={color} opacity="0.75" />
          <path d="M180 120 Q230 100 220 170 Q195 150 178 150 Z" fill={color} opacity="0.75" />
        </>
      );
    if (wingStyle === 2)
      return (
        <>
          <path d="M65 130 L18 110 L28 156 L52 148 Z" fill={accent} opacity="0.8" />
          <path d="M175 130 L222 110 L212 156 L188 148 Z" fill={accent} opacity="0.8" />
        </>
      );
    return (
      <>
        <ellipse cx="42" cy="140" rx="24" ry="10" fill={accent} opacity="0.65" transform="rotate(-20 42 140)" />
        <ellipse cx="198" cy="140" rx="24" ry="10" fill={accent} opacity="0.65" transform="rotate(20 198 140)" />
      </>
    );
  })();

  // Limbs / feet
  const limbs = (() => {
    if (limbStyle === 0)
      return (
        <>
          <ellipse cx="92" cy="216" rx="14" ry="7" fill="#0B1220" opacity="0.85" />
          <ellipse cx="148" cy="216" rx="14" ry="7" fill="#0B1220" opacity="0.85" />
        </>
      );
    if (limbStyle === 1)
      return (
        <>
          <rect x="82" y="204" width="16" height="18" rx="4" fill={accent} />
          <rect x="142" y="204" width="16" height="18" rx="4" fill={accent} />
        </>
      );
    return (
      <>
        <path d="M84 210 L92 226 L104 210 Z" fill={color} />
        <path d="M136 210 L148 226 L160 210 Z" fill={color} />
      </>
    );
  })();

  const ears = (() => {
    if (earStyle === 0)
      return (
        <>
          <path d={`M80 70 L${68 + earTilt} 22 L104 70 Z`} fill={color} opacity="0.9" />
          <path d={`M160 70 L${172 - earTilt} 22 L136 70 Z`} fill={color} opacity="0.9" />
          <path d={`M84 62 L${76 + earTilt} 34 L100 66 Z`} fill={accent} opacity="0.7" />
          <path d={`M156 62 L${164 - earTilt} 34 L140 66 Z`} fill={accent} opacity="0.7" />
        </>
      );
    if (earStyle === 1)
      return (
        <>
          <circle cx="76" cy="66" r="14" fill={color} opacity="0.9" />
          <circle cx="164" cy="66" r="14" fill={color} opacity="0.9" />
          <circle cx="76" cy="66" r="6" fill={accent} opacity="0.85" />
          <circle cx="164" cy="66" r="6" fill={accent} opacity="0.85" />
        </>
      );
    if (earStyle === 2)
      return (
        <>
          <path d={`M78 60 Q${62 + earTilt} 20 96 56`} stroke={color} strokeWidth="8" fill="none" strokeLinecap="round" />
          <path d={`M162 60 Q${178 - earTilt} 20 144 56`} stroke={color} strokeWidth="8" fill="none" strokeLinecap="round" />
        </>
      );
    if (earStyle === 3)
      return (
        <>
          <path d={`M78 66 L${64 + earTilt} 30 L92 46 Z`} fill={color} opacity="0.9" />
          <path d={`M162 66 L${176 - earTilt} 30 L148 46 Z`} fill={color} opacity="0.9" />
        </>
      );
    // Long floppy ears
    return (
      <>
        <path d="M78 66 Q56 90 66 118 Q80 108 88 82 Z" fill={color} opacity="0.85" />
        <path d="M162 66 Q184 90 174 118 Q160 108 152 82 Z" fill={color} opacity="0.85" />
      </>
    );
  })();

  // Horns on top of head, drawn after body
  const horns = (() => {
    if (hornStyle === 0) return null;
    if (hornStyle === 1)
      return (
        <>
          <path d="M104 56 L96 30 L114 50 Z" fill="#F8FAFC" opacity="0.85" />
          <path d="M136 56 L144 30 L126 50 Z" fill="#F8FAFC" opacity="0.85" />
        </>
      );
    return <path d="M120 44 L112 22 L120 34 L128 22 Z" fill={accent} />;
  })();

  // Belly spots / stripes
  const spots = (() => {
    if (spotStyle === 0) return null;
    if (spotStyle === 1)
      return (
        <>
          <circle cx="108" cy="160" r="4" fill={accent} opacity="0.7" />
          <circle cx="132" cy="152" r="3" fill={accent} opacity="0.7" />
          <circle cx="124" cy="172" r="3" fill={accent} opacity="0.7" />
        </>
      );
    if (spotStyle === 2)
      return (
        <path d="M100 140 Q120 148 140 140 M100 158 Q120 166 140 158" stroke={accent} strokeWidth="2" fill="none" opacity="0.7" />
      );
    return (
      <>
        <rect x="112" y="140" width="16" height="4" rx="2" fill={accent} opacity="0.6" />
        <rect x="106" y="152" width="28" height="4" rx="2" fill={accent} opacity="0.6" />
      </>
    );
  })();

  const motif = renderMotif(pal.element, accent, seed);

  return (
    <svg viewBox="0 0 240 240" width={size} height={size} preserveAspectRatio="xMidYMid meet" style={{ maxWidth: "100%", height: "auto" }} aria-label={`${pal.name} portrait`} role="img">
      <defs>
        <radialGradient id={bgId} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor={color} stopOpacity="0.55" />
          <stop offset="55%" stopColor={accent} stopOpacity="0.2" />
          <stop offset="100%" stopColor="#0B1220" stopOpacity="1" />
        </radialGradient>
        <linearGradient id={bodyId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={accent} stopOpacity="0.75" />
        </linearGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="240" height="240" rx="18" fill={`url(#${bgId})`} />
      <circle cx="120" cy="130" r="95" fill={`url(#${glowId})`} />
      {motif}

      <g transform={`rotate(${bodyRot} 120 130)`}>
        {wings}
        {tail}
        {limbs}
        {ears}
        <path d={bodyPath} fill={`url(#${bodyId})`} stroke="#0B1220" strokeWidth="3" />
        {/* belly */}
        <ellipse cx="120" cy={155} rx="34" ry="26" fill="#F8FAFC" opacity="0.15" />
        {spots}
        {horns}
        {/* cheeks */}
        <circle cx={120 - eyeGap - 6} cy={cheekY} r="6" fill={accent} opacity="0.55" />
        <circle cx={120 + eyeGap + 6} cy={cheekY} r="6" fill={accent} opacity="0.55" />
        {/* eyes */}
        <ellipse cx={120 - eyeGap} cy={eyeY} rx="8" ry="10" fill="#0B1220" />
        <ellipse cx={120 + eyeGap} cy={eyeY} rx="8" ry="10" fill="#0B1220" />
        <circle cx={120 - eyeGap + 2} cy={eyeY - 3} r="3" fill="#F8FAFC" />
        <circle cx={120 + eyeGap + 2} cy={eyeY - 3} r="3" fill="#F8FAFC" />
        <circle cx={120 - eyeGap - 2} cy={eyeY + 3} r="1.4" fill="#F8FAFC" opacity="0.7" />
        <circle cx={120 + eyeGap - 2} cy={eyeY + 3} r="1.4" fill="#F8FAFC" opacity="0.7" />
        {/* mouth */}
        <path
          d={`M${120 - mouthW / 2} ${eyeY + 24} Q120 ${eyeY + 34 + (r(18, 6))} ${120 + mouthW / 2} ${eyeY + 24}`}
          stroke="#0B1220"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
      </g>

      {/* element sigil */}
      <g>
        <circle cx="204" cy="36" r="16" fill="#0B1220" opacity="0.78" stroke={color} strokeWidth="1.5" />
        <text x="204" y="41" textAnchor="middle" fontSize="13" fill={color} fontFamily="Inter, sans-serif" fontWeight="800">
          {pal.element[0]}
        </text>
      </g>
    </svg>
  );
}

function renderMotif(element: Element, accent: string, seed: number) {
  const r = (shift: number, mod: number) => (seed >>> shift) % mod;
  switch (element) {
    case "Fire":
      return (
        <g opacity="0.55" fill={accent}>
          <path d="M40 210 Q50 180 40 160 Q60 178 62 210 Z" />
          <path d="M200 210 Q188 175 202 152 Q216 180 214 210 Z" />
          <circle cx={70 + r(20, 40)} cy={40 + r(22, 20)} r="3" />
          <circle cx={170 + r(24, 30)} cy={60 + r(26, 20)} r="2" />
        </g>
      );
    case "Water":
      return (
        <g opacity="0.6" fill={accent}>
          <circle cx="52" cy="200" r="10" opacity="0.7" />
          <circle cx="192" cy="196" r="7" />
          <circle cx="210" cy="170" r="4" />
          <circle cx="34" cy="170" r="5" opacity="0.5" />
          <path d="M20 230 Q120 220 220 230 L220 240 L20 240 Z" opacity="0.35" />
        </g>
      );
    case "Grass":
      return (
        <g opacity="0.65" fill={accent}>
          <path d="M30 220 Q48 190 62 216 Q52 226 30 220 Z" />
          <path d="M210 220 Q192 190 178 216 Q188 226 210 220 Z" />
          <path d="M120 34 L128 22 L120 46 L112 22 Z" opacity="0.7" />
        </g>
      );
    case "Electric":
      return (
        <g stroke={accent} strokeWidth="3" fill="none" opacity="0.8" strokeLinecap="round">
          <path d="M28 60 L46 78 L34 90 L52 110" />
          <path d="M212 60 L194 78 L206 90 L188 110" />
          <path d="M120 20 L124 32 L118 32 L124 46" />
        </g>
      );
    case "Ice":
      return (
        <g stroke={accent} strokeWidth="2" opacity="0.75" fill="none" strokeLinecap="round">
          <path d="M36 46 L52 62 M52 46 L36 62 M44 42 L44 66" />
          <path d="M196 46 L212 62 M212 46 L196 62 M204 42 L204 66" />
          <path d="M120 220 L124 232 L116 232 Z" fill={accent} />
        </g>
      );
    case "Ground":
      return (
        <g fill={accent} opacity="0.55">
          <path d="M10 220 L60 210 L110 220 L160 210 L230 220 L230 240 L10 240 Z" />
          <circle cx="46" cy="52" r="6" />
          <circle cx="196" cy="60" r="5" />
        </g>
      );
    case "Dark":
      return (
        <g opacity="0.7">
          <circle cx="46" cy="46" r="4" fill={accent} />
          <circle cx="196" cy="52" r="3" fill={accent} />
          <circle cx="30" cy="120" r="2" fill={accent} />
          <circle cx="210" cy="150" r="2" fill={accent} />
          <path d="M22 100 Q120 60 218 100" stroke={accent} strokeWidth="1" fill="none" opacity="0.4" />
        </g>
      );
    case "Dragon":
      return (
        <g opacity="0.6" fill={accent}>
          <path d="M20 220 L60 200 L52 226 Z" />
          <path d="M220 220 L180 200 L188 226 Z" />
          <path d="M120 24 L128 40 L120 34 L112 40 Z" />
          <circle cx={60 + r(20, 30)} cy={40 + r(22, 15)} r="2" />
        </g>
      );
    default:
      return (
        <g opacity="0.45" fill={accent}>
          <circle cx="48" cy="52" r="3" />
          <circle cx="196" cy="48" r="3" />
          <circle cx="30" cy="184" r="2" />
          <circle cx="212" cy="188" r="2" />
        </g>
      );
  }
}
